import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from "lucide-react";
import {
  Input,
  Select,
  MultiSelect,
  type MultiSelectOption,
} from "@/components/ui";
import { WalletDateRangeInputs } from "@/components/common/WalletDateRangeInputs";
import {
  DeferredFilterPanel,
  DeferredFilterSection,
} from "@/components/common/DeferredFilterPanel";
import { countHandshakeActiveFilters } from "@/components/handshake/handshakeFilterDefaults";
import { QueryLoadError } from "@/components/common/QueryLoadError";
import { cn } from "@/lib/utils";
import type { QueryLoadFailure } from "@/lib/queryLoadFailure";
import type { HandshakeSessionStatus } from "@/types";
import type { BookingSource } from "@/services/handshakeService";
import {
  SESSION_STATUS_CONFIG,
  handshakeSessionStatusLabel,
  channelLabel,
} from "./config";

export interface HandshakeFiltersState {
  statuses: HandshakeSessionStatus[];
  channels: BookingSource[];
  dateFrom: string;
  dateTo: string;
  customerName: string;
  sellerUserId: string;
}

interface HandshakeFiltersProps {
  formFilters: HandshakeFiltersState;
  appliedFilters: HandshakeFiltersState;
  setFormFilters: (filters: HandshakeFiltersState) => void;
  applyFilters: () => void;
  resetFilters: () => void;
  hasUnappliedChanges: boolean;
  showCustomerNameFilter?: boolean;
  /** Shows the seller filter, fed by the admin sellers query. */
  sellerFilter?: SellerFilterSource;
  className?: string;
}

export interface SellerFilterSource {
  sellers: Array<{ id: string; name: string }>;
  isLoading: boolean;
  loadError: QueryLoadFailure | undefined;
}

export function HandshakeFilters({
  formFilters: filters,
  appliedFilters,
  setFormFilters,
  applyFilters,
  resetFilters,
  hasUnappliedChanges,
  showCustomerNameFilter = true,
  sellerFilter,
  className,
}: HandshakeFiltersProps) {
  const { t: tComponents } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const showSellerFilter = sellerFilter !== undefined;
  const sellers = sellerFilter?.sellers;
  const { t } = useTranslation("handshake");

  const statusOptions: MultiSelectOption[] = (
    Object.keys(SESSION_STATUS_CONFIG) as HandshakeSessionStatus[]
  ).map((status) => ({
    value: status,
    label: handshakeSessionStatusLabel(status),
  }));

  const channelOptions: MultiSelectOption[] = [
    { value: "WHATSAPP", label: channelLabel("WHATSAPP") },
    { value: "SMS", label: channelLabel("SMS") },
    { value: "EMAIL", label: channelLabel("EMAIL") },
  ];
  const sellerOptions = useMemo(
    () => [
      { value: "", label: t("filters.allSellers") },
      ...(sellers ?? []).map((p) => ({ value: p.id, label: p.name })),
    ],
    [t, sellers],
  );

  const updateFilter = <K extends keyof HandshakeFiltersState>(
    key: K,
    value: HandshakeFiltersState[K],
  ) => {
    setFormFilters({ ...filters, [key]: value });
  };

  const activeFiltersCount = useMemo(
    () =>
      countHandshakeActiveFilters(appliedFilters, {
        showCustomerNameFilter,
        showSellerFilter,
      }),
    [appliedFilters, showCustomerNameFilter, showSellerFilter],
  );

  return (
    <DeferredFilterPanel
      variant="compact"
      className={cn(className)}
      panelTitle={t("filters.panelTitle")}
      activeFiltersCount={activeFiltersCount}
      clearAllLabel={t("filters.clearAll")}
      hasUnappliedChanges={hasUnappliedChanges}
      onApply={applyFilters}
      onReset={resetFilters}
      applyLabel={t("filters.search")}
      resetLabel={t("filters.clear")}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MultiSelect
          placeholderLabel={tComponents("multiSelectPlaceholder")}
          removeChipLabel={(chipLabel) =>
            tComponents("multiSelectRemoveChip", { label: chipLabel })
          }
          label={t("filters.status")}
          options={statusOptions}
          value={filters.statuses}
          onChange={(values) =>
            updateFilter("statuses", values as HandshakeSessionStatus[])
          }
        />

        <MultiSelect
          placeholderLabel={tComponents("multiSelectPlaceholder")}
          removeChipLabel={(chipLabel) =>
            tComponents("multiSelectRemoveChip", { label: chipLabel })
          }
          label={t("filters.channel")}
          options={channelOptions}
          value={filters.channels}
          onChange={(values) =>
            updateFilter("channels", values as BookingSource[])
          }
        />

        {showCustomerNameFilter ? (
          <Input
            label={t("filters.customerName")}
            type="text"
            value={filters.customerName}
            onChange={(e) => updateFilter("customerName", e.target.value)}
            placeholder={t("filters.customerNamePlaceholder")}
          />
        ) : null}

        <DeferredFilterSection
          icon={Calendar}
          title={t("filters.dateRange")}
          compact
          className="space-y-1.5 sm:col-span-1"
        >
          <WalletDateRangeInputs
            startDate={filters.dateFrom}
            endDate={filters.dateTo}
            startLabel={t("filters.from")}
            endLabel={t("filters.to")}
            clampToToday={false}
            className="grid grid-cols-2 gap-2"
            onChange={({ startDate, endDate }) =>
              setFormFilters({
                ...filters,
                dateFrom: startDate,
                dateTo: endDate,
              })
            }
          />
        </DeferredFilterSection>

        {sellerFilter ? (
          <div className="space-y-2">
            <Select
              label={t("filters.seller")}
              options={
                sellerFilter.isLoading
                  ? [{ value: filters.sellerUserId, label: tCommon("loading") }]
                  : sellerOptions
              }
              value={filters.sellerUserId}
              disabled={sellerFilter.isLoading}
              onChange={(e) => updateFilter("sellerUserId", e.target.value)}
            />
            {sellerFilter.loadError ? (
              <QueryLoadError failure={sellerFilter.loadError} />
            ) : null}
          </div>
        ) : null}
      </div>
    </DeferredFilterPanel>
  );
}
